import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import DateTimeDisplay from './DateTimeDisplay';
import TimetableNotificationCard from './TimetableNotificationCard';
import TeacherTimetableView from './TeacherTimetableView';
import TeacherAttendance from './TeacherAttendance';
import TeacherAssignments from './TeacherAssignments';
import CallCenter from './CallCenter';
import './CallCenter.css';
import DashboardMail from './DashboardMail';

const MAX_PROGRESS_MEDIA_BYTES = 75 * 1024 * 1024;

const formatMediaSize = (bytes = 0) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
};

const getSupportedVideoMimeType = () => {
  if (typeof window === 'undefined' || !window.MediaRecorder?.isTypeSupported) return '';
  return [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4'
  ].find((type) => window.MediaRecorder.isTypeSupported(type)) || '';
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [feedbackRecords, setFeedbackRecords] = useState([]);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [assignedAdminCards, setAssignedAdminCards] = useState([]);
  const [availableAdminCards, setAvailableAdminCards] = useState([]);
  const [progressMediaForm, setProgressMediaForm] = useState({
    username: '',
    title: '',
    description: '',
    category: 'student-progress',
    media: null
  });
  const [progressMediaMessage, setProgressMediaMessage] = useState('');
  const [progressMediaUploading, setProgressMediaUploading] = useState(false);
  const [progressMediaPreview, setProgressMediaPreview] = useState(null);
  const [cameraMode, setCameraMode] = useState('photo');
  const [cameraFacingMode, setCameraFacingMode] = useState('environment');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraRecording, setCameraRecording] = useState(false);
  const [cameraStatus, setCameraStatus] = useState('');
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const progressPreviewUrlRef = useRef(null);
  const progressFileInputRef = useRef(null);

  useEffect(() => {
    checkTeacherAuth();
  }, []);

  useEffect(() => {
    if (teacher) {
      loadTeacherData();
      loadAdminCards();
    }
  }, [teacher]);

  useEffect(() => {
    filterStudents();
  }, [students, selectedClass, selectedSubject]);

  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (progressPreviewUrlRef.current) {
        URL.revokeObjectURL(progressPreviewUrlRef.current);
      }
    };
  }, []);

  const checkTeacherAuth = () => {
    const teacherUser = localStorage.getItem('teacherUser');
    if (!teacherUser) {
      navigate('/teacher-login');
      return;
    }

    const teacherData = JSON.parse(teacherUser);
    if (teacherData.role !== 'teacher') {
      navigate('/teacher-login');
      return;
    }

    setTeacher(teacherData);
  };

  const loadTeacherData = async () => {
    setLoading(true);
    try {
      // Only fetch teacher details if we don't already have complete data (check for profilePicture)
      if (!teacher.profilePicture || !teacher.profilePicture.startsWith('http')) {
        // Load teacher details from teachers.json - this now includes profile picture
        const teacherResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${teacher.id}`);
        if (teacherResponse.ok) {
          const teacherData = await teacherResponse.json();
          setTeacher(teacherData);
        } else {
          console.warn('Failed to fetch teacher details, using localStorage data');
        }
      }

      // Load all students (teacher will see only assigned classes)
      const studentsResponse = await fetch(`${API_ENDPOINTS.STUDENTS}`);
      if (studentsResponse.ok) {
        const studentsData = await studentsResponse.json();
        setStudents(studentsData);
      }

      // Load classes and subjects
      const [classesRes, subjectsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.CLASSES}`),
        fetch(`${API_ENDPOINTS.SUBJECTS}`)
      ]);

      if (classesRes.ok) {
        const classesData = await classesRes.json();
        setClasses(classesData);
      }

      if (subjectsRes.ok) {
        const subjectsData = await subjectsRes.json();
        setSubjects(subjectsData);
      }

      // Load teacher feedback
      const feedbackResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${teacher.id}/feedback`);
      if (feedbackResponse.ok) {
        const feedbackData = await feedbackResponse.json();
        setFeedbackRecords(feedbackData);
      }

    } catch (error) {
      console.error('Error loading teacher data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterStudents = () => {
    if (!teacher) return;

    let filtered = students;

    // Filter by teacher's assigned classes
    if (teacher.assignedClasses && teacher.assignedClasses.length > 0) {
      filtered = filtered.filter(student => teacher.assignedClasses.includes(student.className));
    }

    // Additional filter by selected class
    if (selectedClass) {
      filtered = filtered.filter(student => student.className === selectedClass);
    }

    // Filter by selected subject (if teacher teaches that subject)
    if (selectedSubject && teacher.assignedSubjects && teacher.assignedSubjects.includes(selectedSubject)) {
      // This would need subject-wise filtering logic
      // For now, just show all students in assigned classes
    }

    setFilteredStudents(filtered);
  };

  const handleLogout = () => {
    localStorage.removeItem('teacherUser');
    navigate('/teacher-login');
  };

  const markStudentAttendance = async (studentId, status) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ATTENDANCE}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId,
          date: new Date().toISOString().split('T')[0],
          status,
          markedBy: teacher.id
        })
      });

      if (response.ok) {
        alert('Attendance marked successfully!');
      } else {
        alert('Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance');
    }
  };

  const submitFeedback = async (feedbackData) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${teacher.id}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData)
      });

      if (response.ok) {
        alert('Feedback/request submitted successfully!');
        setShowFeedbackForm(false);
        loadTeacherData(); // Refresh feedback data
      } else {
        alert('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback');
    }
  };

  const clearProgressMediaFile = () => {
    if (progressPreviewUrlRef.current) {
      URL.revokeObjectURL(progressPreviewUrlRef.current);
      progressPreviewUrlRef.current = null;
    }
    setProgressMediaPreview(null);
    setProgressMediaForm((previous) => ({ ...previous, media: null }));
    if (progressFileInputRef.current) {
      progressFileInputRef.current.value = '';
    }
  };

  const setProgressMediaFile = (file, source) => {
    if (!file) return;

    const fileKind = file.type.startsWith('video/')
      ? 'video'
      : file.type.startsWith('image/')
        ? 'image'
        : '';

    if (!fileKind) {
      clearProgressMediaFile();
      setProgressMediaMessage('Choose a photo or video file.');
      return;
    }

    if (file.size > MAX_PROGRESS_MEDIA_BYTES) {
      clearProgressMediaFile();
      setProgressMediaMessage(`Media must be ${formatMediaSize(MAX_PROGRESS_MEDIA_BYTES)} or smaller. Please record a shorter video or upload a smaller file.`);
      return;
    }

    if (progressPreviewUrlRef.current) {
      URL.revokeObjectURL(progressPreviewUrlRef.current);
    }

    const previewUrl = URL.createObjectURL(file);
    progressPreviewUrlRef.current = previewUrl;
    setProgressMediaPreview({
      url: previewUrl,
      type: fileKind,
      name: file.name,
      size: file.size,
      source
    });
    setProgressMediaForm((previous) => ({ ...previous, media: file }));
    setProgressMediaMessage('');

    if (source === 'camera' && progressFileInputRef.current) {
      progressFileInputRef.current.value = '';
    }
  };

  const stopCamera = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setCameraRecording(false);
    setCameraStatus('');
  };

  const startCamera = async (mode = cameraMode, facingMode = cameraFacingMode) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraStatus('This browser does not support live camera capture. Use photo/video upload instead.');
      return null;
    }

    stopCamera();
    setCameraStatus('Opening camera...');

    const constraints = {
      video: { facingMode: { ideal: facingMode } },
      audio: mode === 'video'
    };

    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (error) {
        if (mode !== 'video') throw error;
        stream = await navigator.mediaDevices.getUserMedia({
          video: constraints.video,
          audio: false
        });
      }

      cameraStreamRef.current = stream;
      setCameraActive(true);

      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        await cameraVideoRef.current.play().catch(() => {});
      }

      setCameraStatus(mode === 'video'
        ? 'Camera is live. Record a short video when ready.'
        : 'Camera is live. Take a photo when ready.');
      return stream;
    } catch (error) {
      console.error('Error starting camera:', error);
      setCameraActive(false);
      setCameraStatus('Camera could not start. Check browser permission or upload a file instead.');
      return null;
    }
  };

  const handleCameraModeChange = async (mode) => {
    if (cameraRecording) return;
    setCameraMode(mode);
    if (cameraActive) {
      await startCamera(mode);
    } else {
      setCameraStatus('');
    }
  };

  const toggleCameraFacing = async () => {
    if (cameraRecording) return;
    const nextFacingMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
    setCameraFacingMode(nextFacingMode);
    if (cameraActive) {
      await startCamera(cameraMode, nextFacingMode);
    }
  };

  const capturePhoto = async () => {
    const stream = cameraStreamRef.current || await startCamera('photo');
    const video = cameraVideoRef.current;
    if (!stream || !video) return;

    if (!video.videoWidth) {
      await new Promise((resolve) => {
        const timer = setTimeout(resolve, 600);
        video.onloadedmetadata = () => {
          clearTimeout(timer);
          resolve();
        };
      });
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(video, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) {
        setCameraStatus('Photo could not be captured. Try again or upload a file.');
        return;
      }
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const file = new File([blob], `progress-photo-${timestamp}.jpg`, { type: 'image/jpeg' });
      setProgressMediaFile(file, 'camera');
      setCameraStatus('Photo captured and ready to send.');
    }, 'image/jpeg', 0.9);
  };

  const startVideoRecording = async () => {
    if (typeof window === 'undefined' || !window.MediaRecorder) {
      setCameraStatus('Video recording is not supported in this browser. Use photo/video upload instead.');
      return;
    }

    const stream = cameraStreamRef.current || await startCamera('video');
    if (!stream) return;

    recordedChunksRef.current = [];
    const mimeType = getSupportedVideoMimeType();
    const options = mimeType ? { mimeType } : undefined;

    try {
      const recorder = new window.MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const recordingType = recorder.mimeType || mimeType || 'video/webm';
        const blob = new Blob(recordedChunksRef.current, { type: recordingType });
        const extension = recordingType.includes('mp4') ? 'mp4' : 'webm';
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const file = new File([blob], `progress-video-${timestamp}.${extension}`, { type: recordingType });
        setProgressMediaFile(file, 'camera');
        setCameraRecording(false);
        setCameraStatus('Video captured and ready to send.');
      };

      recorder.start();
      setCameraRecording(true);
      setCameraStatus('Recording video...');
    } catch (error) {
      console.error('Error recording video:', error);
      setCameraStatus('Video recording could not start. Try upload instead.');
    }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleProgressFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    stopCamera();
    setProgressMediaFile(file, 'upload');
  };

  const submitProgressMedia = async (event) => {
    event.preventDefault();
    setProgressMediaMessage('');
    if (!progressMediaForm.username || !progressMediaForm.media) {
      setProgressMediaMessage('Select a child and attach a photo or video.');
      return;
    }

    const selectedStudent = students.find((student) => student.username === progressMediaForm.username);
    const formData = new FormData();
    formData.append('username', progressMediaForm.username);
    formData.append('teacherId', teacher.id);
    formData.append('teacherEmail', teacher.email || '');
    formData.append('title', progressMediaForm.title || `${selectedStudent?.firstName || 'Student'} progress update`);
    formData.append('description', progressMediaForm.description);
    formData.append('category', progressMediaForm.category);
    formData.append('media', progressMediaForm.media);

    setProgressMediaUploading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/progress-media/teacher-upload`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Upload failed');
      setProgressMediaMessage('Progress update uploaded to the school gallery and linked parent dashboard.');
      setProgressMediaForm({
        username: '',
        title: '',
        description: '',
        category: 'student-progress',
        media: null
      });
      clearProgressMediaFile();
      stopCamera();
      event.target.reset();
    } catch (error) {
      setProgressMediaMessage(error.message || 'Failed to upload progress update.');
    } finally {
      setProgressMediaUploading(false);
    }
  };

  const loadAdminCards = async () => {
    try {
      // Load available admin cards
      const availableResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/admin-cards/available`);
      if (availableResponse.ok) {
        const availableData = await availableResponse.json();
        setAvailableAdminCards(availableData.adminCards);
      }

      // Load assigned admin cards
      const assignedResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${teacher.id}/admin-cards`);
      if (assignedResponse.ok) {
        const assignedData = await assignedResponse.json();
        setAssignedAdminCards(assignedData.assignedAdminCards);
      }
    } catch (error) {
      console.error('Error loading admin cards:', error);
    }
  };

  const handleAdminCardClick = (cardId) => {
    // Find the card details
    const card = availableAdminCards.find(c => c.id === cardId);
    if (card) {
      navigate(card.path);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading teacher dashboard...</div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-600">Access denied. Please login as a teacher.</div>
      </div>
    );
  }

  return (
    <div className="app-vibrant-shell p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="app-vibrant-header flex flex-col lg:flex-row justify-between lg:items-center gap-4 mb-8 p-5 md:p-6">
          <div className="flex flex-wrap items-center gap-3">
            {/* Profile Picture */}
            <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-sky-200 shadow-lg">
              <img
                src={teacher.profilePicture || '/fallback-data/default-avatar.png'}
                alt={`${teacher.firstName} ${teacher.lastName}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/fallback-data/default-avatar.png';
                }}
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-950">Teacher Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Welcome back, {teacher.firstName} {teacher.lastName}
              </p>
              <p className="text-sm text-gray-500">
                Employee ID: {teacher.employeeId} | Subjects: {teacher.assignedSubjects?.join(', ') || 'None assigned'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <DateTimeDisplay />
            <button
              onClick={handleLogout}
              className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition duration-300 shadow-md"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Timetable Notification Card */}
        <div className="mb-6">
          <TimetableNotificationCard userType="teacher" teacherId={teacher.id} />
        </div>

        <DashboardMail userType="teacher" user={teacher} />

        {/* Dashboard Navigation Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-7">
          <div className="xl:col-span-1">
            <CallCenter
              identity={{
                type: 'teacher',
                id: teacher.id,
                name: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() || teacher.name || teacher.id
              }}
            />
          </div>
          {[
            ['overview', 'Overview', `${filteredStudents.length} students · ${teacher.assignedClasses?.length || 0} classes`, '#0ea5e9', '#e0f2fe'],
            ['my-attendance', 'My Attendance', 'View your attendance record and rate', '#10b981', '#d1fae5'],
            ['timetable', 'My Timetable', `${teacher.assignedSubjects?.length || 0} assigned subjects`, '#8b5cf6', '#ede9fe'],
            ['assignments', 'Assignments', 'Create, review, and manage class work', '#f59e0b', '#fef3c7'],
            ['students', 'My Students', `${filteredStudents.length} students currently available`, '#ec4899', '#fce7f3'],
            ['progress', 'Student Progress', 'Record grades and review performance', '#14b8a6', '#ccfbf1'],
            ['parent-updates', 'Parent Updates', 'Send photos, videos, and progress notes', '#06b6d4', '#cffafe'],
            ['feedback', 'Feedback', `${feedbackRecords.length} feedback records`, '#f97316', '#ffedd5'],
            ['manage', 'Manage', `${assignedAdminCards.length} assigned admin functions`, '#6366f1', '#e0e7ff']
          ].map(([id, title, detail, color, soft]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`text-left min-h-[150px] p-5 rounded-2xl border-4 transition hover:-translate-y-1 shadow-lg ${
                activeTab === id ? 'ring-4 ring-slate-300' : ''
              }`}
              style={{ borderColor: color, background: `linear-gradient(180deg, ${soft}, #fff)` }}
            >
              <span className="block text-xs font-black uppercase text-slate-500">Teacher workspace</span>
              <strong className="block text-xl text-slate-950 mt-3">{title}</strong>
              <span className="block text-sm text-slate-600 mt-2">{detail}</span>
              <span className="block h-2 rounded-full mt-5" style={{ backgroundColor: color }} />
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#0ea5e9', '--card-soft': '#e0f2fe' }}>
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Students</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredStudents.length}</p>
                </div>
              </div>
            </div>

            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#10b981', '--card-soft': '#d1fae5' }}>
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Classes</p>
                  <p className="text-2xl font-bold text-gray-900">{teacher.assignedClasses?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#8b5cf6', '--card-soft': '#ede9fe' }}>
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-full">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Subjects</p>
                  <p className="text-2xl font-bold text-gray-900">{teacher.assignedSubjects?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#f59e0b', '--card-soft': '#fef3c7' }}>
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Experience</p>
                  <p className="text-2xl font-bold text-gray-900">{teacher.experience} years</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* My Attendance Tab */}
        {activeTab === 'my-attendance' && (
          <TeacherAttendance />
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#0ea5e9', '--card-soft': '#e0f2fe' }}>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Filter Students</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Classes</option>
                    {teacher.assignedClasses?.map((className) => (
                      <option key={className} value={className}>{className}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <select
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Subjects</option>
                    {teacher.assignedSubjects?.map((subject) => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Students List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStudents.map((student) => (
                <div key={student.username} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800">
                        {student.firstName} {student.lastName}
                      </h4>
                      <p className="text-sm text-gray-600">Class: {student.className}</p>
                      <p className="text-sm text-gray-600">Username: {student.username}</p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <p><strong>Age:</strong> {student.age}</p>
                    <p><strong>Parent Contact:</strong> {student.parentContact}</p>
                  </div>

                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => markStudentAttendance(student.username, 'present')}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                    >
                      Present
                    </button>
                    <button
                      onClick={() => markStudentAttendance(student.username, 'absent')}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Absent
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredStudents.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No students found for the selected filters.</p>
              </div>
            )}
          </div>
        )}



        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <TeacherAssignments teacherId={teacher.employeeId} />
        )}

        {/* Timetable Tab */}
        {activeTab === 'timetable' && (
          <TeacherTimetableView teacherId={teacher.id} />
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#10b981', '--card-soft': '#d1fae5' }}>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Student Progress Tracking</h3>
            <p className="text-gray-600">
              Progress tracking features will be available soon. You'll be able to view and update student grades, achievements, and learning progress.
            </p>
          </div>
        )}

        {activeTab === 'parent-updates' && (
          <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#06b6d4', '--card-soft': '#cffafe' }}>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Send Parent Progress Update</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Select a child, capture or upload a photo/video, and send it to the linked parent dashboard and school gallery.
                </p>
              </div>
            </div>

            {progressMediaMessage && (
              <div className={`mb-5 rounded-lg p-3 text-sm ${progressMediaMessage.includes('uploaded') ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}>
                {progressMediaMessage}
              </div>
            )}

            <form onSubmit={submitProgressMedia} className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <label className="block">
                <span className="block text-sm font-semibold text-gray-700 mb-1">Child</span>
                <select
                  value={progressMediaForm.username}
                  onChange={(e) => setProgressMediaForm({ ...progressMediaForm, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                >
                  <option value="">Select a child</option>
                  {filteredStudents.map((student) => (
                    <option key={student.username} value={student.username}>
                      {student.firstName} {student.lastName} - {student.className}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-sm font-semibold text-gray-700 mb-1">Title</span>
                <input
                  type="text"
                  value={progressMediaForm.title}
                  onChange={(e) => setProgressMediaForm({ ...progressMediaForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Reading practice, art work, class activity..."
                />
              </label>

              <label className="block lg:col-span-2">
                <span className="block text-sm font-semibold text-gray-700 mb-1">Progress Note</span>
                <textarea
                  value={progressMediaForm.description}
                  onChange={(e) => setProgressMediaForm({ ...progressMediaForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  rows="4"
                  placeholder="Tell the parent what the child did well, what improved, or what to practice at home."
                />
              </label>

              <label className="block">
                <span className="block text-sm font-semibold text-gray-700 mb-1">Category</span>
                <select
                  value={progressMediaForm.category}
                  onChange={(e) => setProgressMediaForm({ ...progressMediaForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="student-progress">Student Progress</option>
                  <option value="class-activity">Class Activity</option>
                  <option value="achievement">Achievement</option>
                  <option value="behavior">Behavior</option>
                  <option value="home-practice">Home Practice</option>
                </select>
              </label>

              <div className="lg:col-span-2">
                <span className="block text-sm font-semibold text-gray-700 mb-2">Photo or Video</span>
                <div className="rounded-lg border border-cyan-100 bg-white p-4">
                  <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                    <div className="xl:col-span-3">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => handleCameraModeChange('photo')}
                          disabled={cameraRecording}
                          className={`px-4 py-2 rounded-md text-sm font-semibold border ${cameraMode === 'photo' ? 'bg-cyan-700 text-white border-cyan-700' : 'bg-white text-cyan-800 border-cyan-200 hover:bg-cyan-50'} disabled:opacity-50`}
                        >
                          Live Photo
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCameraModeChange('video')}
                          disabled={cameraRecording}
                          className={`px-4 py-2 rounded-md text-sm font-semibold border ${cameraMode === 'video' ? 'bg-cyan-700 text-white border-cyan-700' : 'bg-white text-cyan-800 border-cyan-200 hover:bg-cyan-50'} disabled:opacity-50`}
                        >
                          Live Video
                        </button>
                        <button
                          type="button"
                          onClick={() => startCamera(cameraMode)}
                          disabled={cameraRecording}
                          className="px-4 py-2 rounded-md text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-300"
                        >
                          {cameraActive ? 'Restart Camera' : 'Start Camera'}
                        </button>
                        <button
                          type="button"
                          onClick={toggleCameraFacing}
                          disabled={!cameraActive || cameraRecording}
                          className="px-4 py-2 rounded-md text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Switch Camera
                        </button>
                        <button
                          type="button"
                          onClick={stopCamera}
                          disabled={!cameraActive || cameraRecording}
                          className="px-4 py-2 rounded-md text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Stop
                        </button>
                      </div>

                      <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-900">
                        <video
                          ref={cameraVideoRef}
                          className={`h-full w-full object-cover ${cameraActive ? 'block' : 'hidden'}`}
                          autoPlay
                          muted
                          playsInline
                        />
                        {!cameraActive && (
                          <div className="absolute inset-0 flex items-center justify-center px-5 text-center text-sm text-gray-200">
                            Start the camera to capture a live photo or record a video.
                          </div>
                        )}
                        {cameraRecording && (
                          <div className="absolute left-3 top-3 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white">
                            Recording
                          </div>
                        )}
                      </div>

                      {cameraStatus && (
                        <p className="mt-2 text-sm text-gray-600">{cameraStatus}</p>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        {cameraMode === 'photo' ? (
                          <button
                            type="button"
                            onClick={capturePhoto}
                            disabled={cameraRecording}
                            className="bg-cyan-700 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-cyan-800 disabled:bg-gray-300"
                          >
                            Take Photo
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={startVideoRecording}
                              disabled={cameraRecording}
                              className="bg-cyan-700 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-cyan-800 disabled:bg-gray-300"
                            >
                              Start Recording
                            </button>
                            <button
                              type="button"
                              onClick={stopVideoRecording}
                              disabled={!cameraRecording}
                              className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-red-700 disabled:bg-gray-300"
                            >
                              Stop & Use Video
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="xl:col-span-2">
                      <label className="block">
                        <span className="block text-sm font-semibold text-gray-700 mb-1">Upload Photo or Video</span>
                        <input
                          ref={progressFileInputRef}
                          type="file"
                          accept="image/*,video/*"
                          onChange={handleProgressFileUpload}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">Upload an existing file, or use the live camera tools on the left.</p>

                      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <span className="text-sm font-semibold text-gray-700">Selected Media</span>
                          {progressMediaPreview && (
                            <button
                              type="button"
                              onClick={clearProgressMediaFile}
                              className="text-xs font-semibold text-red-600 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        {progressMediaPreview ? (
                          <div>
                            <div className="aspect-video overflow-hidden rounded-md bg-gray-900">
                              {progressMediaPreview.type === 'video' ? (
                                <video
                                  src={progressMediaPreview.url}
                                  className="h-full w-full object-cover"
                                  controls
                                  playsInline
                                />
                              ) : (
                                <img
                                  src={progressMediaPreview.url}
                                  alt="Selected progress update"
                                  className="h-full w-full object-cover"
                                />
                              )}
                            </div>
                            <p className="mt-2 truncate text-sm font-semibold text-gray-700">{progressMediaPreview.name}</p>
                            <p className="text-xs text-gray-500">
                              {progressMediaPreview.source === 'camera' ? 'Captured from live camera' : 'Uploaded file'} · {formatMediaSize(progressMediaPreview.size)}
                            </p>
                          </div>
                        ) : (
                          <div className="flex aspect-video items-center justify-center rounded-md border border-dashed border-gray-300 bg-white px-4 text-center text-sm text-gray-500">
                            No photo or video selected yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 flex justify-end">
                <button
                  type="submit"
                  disabled={progressMediaUploading}
                  className="bg-cyan-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-cyan-800 disabled:bg-gray-300"
                >
                  {progressMediaUploading ? 'Uploading...' : 'Send Update'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="space-y-6">
            {/* Submit Feedback Button */}
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#8b5cf6', '--card-soft': '#ede9fe' }}>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">My Feedback & Requests</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Submit feedback or requests to administration. Track the status of your submissions.
                  </p>
                </div>
                <button
                  onClick={() => setShowFeedbackForm(true)}
                  className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700"
                >
                  Submit New Request
                </button>
              </div>
            </div>

            {/* Feedback Records */}
            <div className="space-y-4">
              {feedbackRecords.length > 0 ? (
                feedbackRecords.map((record) => (
                  <div key={record.id} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-lg font-semibold text-gray-800">{record.title}</h4>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            record.type === 'performance_feedback' ? 'bg-blue-100 text-blue-800' :
                            record.type === 'leave_request' ? 'bg-green-100 text-green-800' :
                            record.type === 'equipment_request' ? 'bg-orange-100 text-orange-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {record.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            record.status === 'approved' ? 'bg-green-100 text-green-800' :
                            record.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {record.status.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">{record.message}</p>
                        <p className="text-sm text-gray-500">
                          Submitted: {new Date(record.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Request Details */}
                    {record.requestDetails && (
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Request Details:</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          {record.requestDetails.startDate && (
                            <p><strong>Start Date:</strong> {record.requestDetails.startDate}</p>
                          )}
                          {record.requestDetails.endDate && (
                            <p><strong>End Date:</strong> {record.requestDetails.endDate}</p>
                          )}
                          {record.requestDetails.reason && (
                            <p><strong>Reason:</strong> {record.requestDetails.reason}</p>
                          )}
                          {record.requestDetails.item && (
                            <p><strong>Item:</strong> {record.requestDetails.item}</p>
                          )}
                          {record.requestDetails.justification && (
                            <p><strong>Justification:</strong> {record.requestDetails.justification}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Admin Response */}
                    {record.adminComments && (
                      <div className="mt-4 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                        <p className="text-sm font-medium text-blue-700">Admin Response:</p>
                        <p className="text-sm text-blue-600">{record.adminComments}</p>
                        {record.reviewedAt && (
                          <p className="text-xs text-blue-500 mt-1">
                            Reviewed: {new Date(record.reviewedAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No feedback submitted yet</h3>
                  <p className="text-gray-500 mb-4">
                    Use the "Submit New Request" button to send feedback or make requests to administration.
                  </p>
                  <button
                    onClick={() => setShowFeedbackForm(true)}
                    className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700"
                  >
                    Submit Your First Request
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manage Tab */}
        {activeTab === 'manage' && (
          <div className="space-y-6">
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#f59e0b', '--card-soft': '#fef3c7' }}>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Assigned Admin Cards</h3>
              <p className="text-gray-600 mb-6">
                These are the administrative functions you have been assigned to manage. Click on any card to access the corresponding feature.
              </p>

              {assignedAdminCards.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {assignedAdminCards.map((cardId) => {
                    const card = availableAdminCards.find(c => c.id === cardId);
                    if (!card) return null;

                    return (
                      <div
                        key={cardId}
                        onClick={() => handleAdminCardClick(cardId)}
                        className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-lg p-6 hover:shadow-xl cursor-pointer transition duration-300 border border-blue-200 hover:border-blue-300"
                      >
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="text-3xl">{card.icon}</div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-800">{card.title}</h4>
                            <p className="text-sm text-gray-600">{card.description}</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            card.category === 'academic' ? 'bg-blue-100 text-blue-800' :
                            card.category === 'finance' ? 'bg-green-100 text-green-800' :
                            card.category === 'hr' ? 'bg-purple-100 text-purple-800' :
                            card.category === 'communication' ? 'bg-orange-100 text-orange-800' :
                            card.category === 'monitoring' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {card.category.toUpperCase()}
                          </span>
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Admin Cards Assigned</h3>
                  <p className="text-gray-500">
                    You haven't been assigned any administrative functions yet. Contact your administrator to request access to specific management features.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Feedback Form Modal */}
        {showFeedbackForm && (
          <FeedbackForm
            onSubmit={submitFeedback}
            onCancel={() => setShowFeedbackForm(false)}
          />
        )}
      </div>
    </div>
  );
};

// Feedback Form Component
const FeedbackForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    type: 'performance_feedback',
    title: '',
    message: '',
    requestDetails: {
      startDate: '',
      endDate: '',
      reason: '',
      item: '',
      justification: ''
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRequestDetailChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      requestDetails: {
        ...prev.requestDetails,
        [field]: value
      }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Submit Feedback or Request</h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              Ã—
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="performance_feedback">Performance Feedback</option>
                <option value="leave_request">Leave Request</option>
                <option value="equipment_request">Equipment Request</option>
                <option value="maintenance_request">Maintenance Request</option>
                <option value="other_request">Other Request</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Brief title for your request/feedback"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <textarea
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows="4"
                placeholder="Detailed description of your request or feedback"
                required
              />
            </div>

            {/* Conditional fields based on request type */}
            {(formData.type === 'leave_request') && (
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium text-gray-800">Leave Request Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={formData.requestDetails.startDate}
                      onChange={(e) => handleRequestDetailChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={formData.requestDetails.endDate}
                      onChange={(e) => handleRequestDetailChange('endDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <select
                    value={formData.requestDetails.reason}
                    onChange={(e) => handleRequestDetailChange('reason', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select reason</option>
                    <option value="vacation">Vacation</option>
                    <option value="sick_leave">Sick Leave</option>
                    <option value="personal">Personal</option>
                    <option value="maternity">Maternity</option>
                    <option value="bereavement">Bereavement</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            )}

            {(formData.type === 'equipment_request') && (
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium text-gray-800">Equipment Request Details</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Requested</label>
                  <input
                    type="text"
                    value={formData.requestDetails.item}
                    onChange={(e) => handleRequestDetailChange('item', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="What equipment do you need?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Justification</label>
                  <textarea
                    value={formData.requestDetails.justification}
                    onChange={(e) => handleRequestDetailChange('justification', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Why do you need this equipment?"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Submit Request
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;
